import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import cn from 'classnames';
import { scrollToId, scrollToTop } from 'shared/lib/scrollTo';
import Header from 'userportal/components/Header';
import Navigation from 'userportal/components/Navigation';
import styles from './Page.module.scss';

const Page = ({
  header,
  subheader,
  centered,
  initialRow,
  initialContainer,
  children,
  className,
  relative,
  helmetTitle,
  helmetDescription,
  seoUrl,
  ogImage,
  noTopPadding,
  withSplash,
  withBackground,
  noNavigationBottomPadding,
}) => {
  const { t } = useTranslation();
  const [menuOpened, setMenuOpened] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpened(false);
    if (location.hash) {
      try {
        setTimeout(() => {
          scrollToId(location.hash);
        });
      } catch {}
    } else {
      scrollToTop();
    }
  }, [location]);

  useEffect(() => {
    if (location.hash) {
      try {
        setTimeout(() => {
          scrollToId(location.hash);
        }, 1000);
      } catch {}
    }
  }, []);

  const pageTitle = helmetTitle || t('common:seoTitle');
  const pageDescription = helmetDescription || t('common:seoDescription');

  return (
    <>
      {(helmetTitle || helmetDescription) && (
        <Helmet>
          <title>{pageTitle}</title>
          <meta property="og:title" content={pageTitle} />
          <meta name="description" content={pageDescription} />
          <meta property="og:description" content={pageDescription} />
          {seoUrl && [
            <link key="canonical" rel="canonical" href={seoUrl} />,
            <meta key="og:url" property="og:url" content={seoUrl} />
          ]}
          <meta property="og:image" content={ogImage || '/skilleto-social.png'} />
        </Helmet>
      )}
      <div className={cn(styles.top, { [styles.withBg]: withBackground })}>
        <Navigation
          menuOpened={menuOpened}
          onToggleMenu={setMenuOpened}
          noNavigationBottomPadding={noNavigationBottomPadding}
        />
        {!menuOpened && header && (
          <Header>{header}</Header>
        )}
        {withBackground && <div className={styles.bg} />}
        {/* <span className={styles.mover}>
          <i className="la la-arrow-down" />
        </span> */}
      </div>
      {!menuOpened && (
        <>
          {subheader}

          <section className={cn(styles.pageSection, { [styles.withSplash]: withSplash })}>
            {initialContainer ? (
              <div className={cn('container', styles.container, className, {
                [styles.relative]: relative,
                [styles.noTopPadding]: noTopPadding,
              })}
              >
                {initialRow || centered ? (
                  <div className={cn({ row: initialRow, 'justify-content-center': centered })}>
                    {children}
                  </div>
                ) : children}
              </div>
            ) : children}
          </section>

          {withSplash && <div className={styles.splash} />}
        </>
      )}
    </>
  );
};

Page.propTypes = {
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  subheader: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  centered: PropTypes.bool,
  initialRow: PropTypes.bool,
  initialContainer: PropTypes.bool,
  relative: PropTypes.bool,
  className: PropTypes.string,
  helmetTitle: PropTypes.string,
  helmetDescription: PropTypes.string,
  seoUrl: PropTypes.string,
  ogImage: PropTypes.string,
  withSplash: PropTypes.bool,
  withBackground: PropTypes.bool,
  noNavigationBottomPadding: PropTypes.bool,
};

Page.defaultProps = {
  header: null,
  subheader: null,
  centered: false,
  initialRow: true,
  initialContainer: true,
  relative: true,
  className: '',
  helmetTitle: null,
  helmetDescription: null,
  seoUrl: null,
  ogImage: null,
  withSplash: false,
  withBackground: true,
  noNavigationBottomPadding: false,
};

export default Page;
